import { collection, orderBy, query, where } from 'firebase/firestore';

/**
 * Tenant-scoped tickets query. Always requires companyId for non–super-admin UIs.
 * @param {import('firebase/firestore').Firestore} db
 * @param {{
 *   companyId: string,
 *   archived?: boolean,
 *   assigneeUid?: string | null,
 *   orderField?: string,
 *   orderDirection?: 'asc' | 'desc',
 * }} opts
 * @returns {import('firebase/firestore').Query | null}
 */
export function buildCompanyTicketsQuery(db, opts) {
  const companyId =
    typeof opts?.companyId === 'string' ? opts.companyId.trim() : '';
  if (!companyId) return null;

  const constraints = [where('companyId', '==', companyId)];

  if (typeof opts.archived === 'boolean') {
    constraints.push(where('archived', '==', opts.archived));
  }

  if (opts.assigneeUid) {
    constraints.push(where('assignedUsers', 'array-contains', opts.assigneeUid));
  }

  const orderField =
    typeof opts.orderField === 'string' && opts.orderField.trim()
      ? opts.orderField.trim()
      : 'updatedAt';
  const orderDirection = opts.orderDirection === 'asc' ? 'asc' : 'desc';
  constraints.push(orderBy(orderField, orderDirection));

  return query(collection(db, 'tickets'), ...constraints);
}

/**
 * Tenant-scoped ticketLogs query.
 * @param {import('firebase/firestore').Firestore} db
 * @param {{
 *   companyId: string,
 *   participantUid?: string | null,
 *   maxRows?: number,
 * }} opts
 * @param {typeof import('firebase/firestore').limit} [limitFn]
 * @returns {import('firebase/firestore').Query | null}
 */
export function buildCompanyTicketLogsConstraints(opts) {
  const companyId =
    typeof opts?.companyId === 'string' ? opts.companyId.trim() : '';
  if (!companyId) return null;

  const constraints = [where('companyId', '==', companyId)];
  if (opts.participantUid) {
    constraints.push(
      where('participantUids', 'array-contains', opts.participantUid),
    );
  }
  constraints.push(orderBy('createdAt', 'desc'));
  return constraints;
}
